export type ContactStatus = 'lead' | 'qualified' | 'active' | 'archived';
export type ContactType = 'person' | 'organization';

export interface FoundationContact {
  id: string;
  realmId: string;
  name: string;
  type: ContactType;
  status: ContactStatus;
  email: string | null;
  phone: string | null;
  address: string | null;
  tags: string[];
  notes: string | null;
  lastContactDate: string | null;
  contactAttempts: number;
  createdAt: string;
  updatedAt: string;
}

export type ContactActivityType = 'created' | 'updated' | 'status_changed' | 'contact_attempt' | 'note' | 'archived';

export interface ContactActivity {
  id: string;
  type: ContactActivityType;
  title: string;
  description: string | null;
  occurredAt: string;
  actorUserId: string | null;
  metadata: Record<string, unknown>;
}

export interface ContactActivityMutation {
  type?: 'note' | 'call' | 'email' | 'meeting' | 'other';
  title?: string;
  description?: string | null;
  occurredAt?: string;
}

export interface ContactListResponse {
  contacts: FoundationContact[];
  total: number;
}

export interface ContactMutation {
  name?: string;
  type?: ContactType;
  status?: ContactStatus;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  tags?: string[];
  notes?: string | null;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
}

function authHeaders(token?: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function parseEnvelope<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }

  const envelope = await response.json() as ApiEnvelope<T>;
  if (!response.ok || envelope.success === false || !envelope.data) {
    throw new Error(envelope.error || `Contacts request failed with ${response.status}`);
  }

  return envelope.data;
}

export async function listFoundationContacts(
  token: string | undefined,
  filters: { search?: string; status?: ContactStatus | ''; type?: ContactType | '' } = {},
): Promise<ContactListResponse> {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.status) params.set('status', filters.status);
  if (filters.type) params.set('type', filters.type);

  const response = await fetch(`/api/v1/contacts?${params.toString()}`, {
    headers: authHeaders(token),
  });

  return parseEnvelope<ContactListResponse>(response);
}

export async function getFoundationContact(
  token: string | undefined,
  contactId: string,
): Promise<FoundationContact> {
  const response = await fetch(`/api/v1/contacts/${encodeURIComponent(contactId)}`, {
    headers: authHeaders(token),
  });

  const data = await parseEnvelope<{ contact: FoundationContact }>(response);
  return data.contact;
}

export async function createFoundationContact(
  token: string | undefined,
  contact: ContactMutation,
): Promise<FoundationContact> {
  const response = await fetch('/api/v1/contacts', {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(contact),
  });

  const data = await parseEnvelope<{ contact: FoundationContact }>(response);
  return data.contact;
}

export async function updateFoundationContact(
  token: string | undefined,
  contactId: string,
  contact: ContactMutation,
): Promise<FoundationContact> {
  const response = await fetch(`/api/v1/contacts/${encodeURIComponent(contactId)}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(contact),
  });

  const data = await parseEnvelope<{ contact: FoundationContact }>(response);
  return data.contact;
}

export async function listFoundationContactActivity(
  token: string | undefined,
  contactId: string,
): Promise<{ contact: FoundationContact; activities: ContactActivity[] }> {
  const response = await fetch(`/api/v1/contacts/${encodeURIComponent(contactId)}/activity`, {
    headers: authHeaders(token),
  });

  return parseEnvelope<{ contact: FoundationContact; activities: ContactActivity[] }>(response);
}

export async function recordFoundationContactActivity(
  token: string | undefined,
  contactId: string,
  activity: ContactActivityMutation,
): Promise<{ contact: FoundationContact; activity: ContactActivity }> {
  const response = await fetch(`/api/v1/contacts/${encodeURIComponent(contactId)}/activity`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(activity),
  });

  return parseEnvelope<{ contact: FoundationContact; activity: ContactActivity }>(response);
}

export async function archiveFoundationContact(
  token: string | undefined,
  contactId: string,
): Promise<void> {
  const response = await fetch(`/api/v1/contacts/${encodeURIComponent(contactId)}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    await parseEnvelope<never>(response);
  }
}
